import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header<{ $scrolled: boolean; $isHome: boolean }>`
  background: ${({ $scrolled, $isHome }) =>
    $scrolled ? 'rgba(255, 255, 255, 0.95)' : $isHome ? 'transparent' : '#fff'};
  box-shadow: ${({ $scrolled, $isHome }) =>
    $scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : $isHome ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.1)'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(10px)' : 'none')};
  color: ${({ $scrolled, $isHome }) => ($scrolled || !$isHome ? '#333' : '#fff')};
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;

  @media (max-width: 768px) {
    height: 60px;
    padding: 0 20px;
  }
`;

const Logo = styled(Link) <{ $scrolled: boolean; $isHome: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ $scrolled, $isHome }) => ($scrolled || !$isHome ? '#2c5aa0' : '#fff')};
  text-decoration: none;
  z-index: 1001;

  @media (max-width: 768px) {
    font-size: 16px;
    gap: 8px;
  }
`;

const LogoImage = styled.img`
  height: 45px;
  width: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 35px;
  }
`;

const LogoText = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const NavMenu = styled.nav<{ $isOpen: boolean; $scrolled: boolean; $isHome: boolean }>`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 1024px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
    z-index: 1000;
    color: #333;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  
  &:hover .dropdown-menu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background: #fff;
  min-width: 220px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 10px 0;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;

  @media (max-width: 1024px) {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    box-shadow: none;
    background: #f8f9fa;
    margin-top: 10px;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    
    &.home-hidden {
      display: none;
    }
  }
`;

const dropdownLinkStyles = `
  display: block;
  padding: 12px 25px;
  color: #333;
  text-decoration: none;
  font-size: 15px;
  transition: background 0.3s ease, color 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f8f9fa;
    color: #2c5aa0;
  }

  @media (max-width: 1024px) {
    padding: 15px;
    font-size: 16px;
    text-align: center;
  }
`;

const DropdownLink = styled(Link)`
  ${dropdownLinkStyles}
`;

const ExternalDropdownLink = styled.a`
  ${dropdownLinkStyles}
`;

const DropdownTrigger = styled.span<{ $isActive: boolean; $scrolled: boolean; $isHome: boolean }>`
  color: ${({ $isActive, $scrolled, $isHome }) =>
    $isActive ? ($scrolled || !$isHome ? '#2c5aa0' : '#fff') :
      ($scrolled || !$isHome ? '#333' : 'rgba(255,255,255,0.9)')};
  font-weight: 500;
  transition: color 0.3s ease;
  font-size: 17px;
  cursor: pointer;
  padding: 10px 0;
  border-bottom: 2px solid transparent;

  &:hover {
    color: ${({ $scrolled, $isHome }) => ($scrolled || !$isHome ? '#2c5aa0' : '#fff')};
    opacity: 1;
  }
  
  ${({ $isActive }) => $isActive && `
    border-bottom-color: currentColor;
  `}

  @media (max-width: 1024px) {
    color: #333;
    font-size: 20px;
  }
`;

const NavLink = styled(Link) <{ $isActive: boolean; $scrolled: boolean; $isHome: boolean }>`
  color: ${({ $isActive, $scrolled, $isHome }) =>
    $isActive ? ($scrolled || !$isHome ? '#2c5aa0' : '#fff') :
      ($scrolled || !$isHome ? '#333' : 'rgba(255,255,255,0.9)')};
  font-weight: 500;
  transition: color 0.3s ease;
  font-size: 17px;
  padding: 10px 0;
  border-bottom: 2px solid transparent;

  &:hover {
    color: ${({ $scrolled, $isHome }) => ($scrolled || !$isHome ? '#2c5aa0' : '#fff')};
    opacity: 1;
  }

  ${({ $isActive }) => $isActive && `
    border-bottom-color: currentColor;
  `}

  @media (max-width: 1024px) {
    color: #333;
    font-size: 20px;
  }
`;

const MobileMenuButton = styled.button<{ $scrolled: boolean; $isHome: boolean; $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ $isOpen, $scrolled, $isHome }) =>
    $isOpen ? '#333' : ($scrolled || !$isHome ? '#333' : '#fff')};
  cursor: pointer;
  z-index: 1002;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    {
      label: '환영합니다',
      subItems: [
        { path: '/greetings', label: '인사말' },
        { path: '/directions', label: '오시는 길' },
        { path: '/vision', label: '교회 비전' },
      ]
    },
    { path: '/worship', label: '교회 소개' },
    { path: '/youth', label: '다음세대' },
    {
      label: '설교',
      subItems: [
        { path: '/sermons', label: '주일설교' },
        { path: '/friday-prayer', label: '금요성령기도회 설교' },
        { path: '/sunday-worship', label: '주일예배' },
      ]
    },
    {
      label: '교회 소식',
      subItems: [
        { path: '/news', label: '공지사항' },
        { path: '/bulletin', label: '주보' },
        { path: '/media', label: '사진/영상' },
      ]
    },
    { path: '/offering', label: '헌금 안내' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (item: any) => {
    if (item.path === location.pathname) return true;
    if (item.subItems) {
      return item.subItems.some((sub: any) => sub.path === location.pathname);
    }
    return false;
  };

  return (
    <HeaderContainer $scrolled={scrolled} $isHome={isHome}>
      <NavContainer>
        <Logo to="/" $scrolled={scrolled} $isHome={isHome}>
          <LogoImage
            src="/logo.png"
            alt="IJBC 얼바인중앙침례교회 로고"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <LogoText>얼바인 중앙침례교회</LogoText>
        </Logo>

        <NavMenu $isOpen={isMenuOpen} $scrolled={scrolled} $isHome={isHome}>
          {navItems.map((item) => (
            item.subItems ? (
              <DropdownContainer key={item.label}>
                <DropdownTrigger
                  $isActive={isActive(item)}
                  $scrolled={scrolled}
                  $isHome={isHome}
                >
                  {item.label}
                </DropdownTrigger>
                <DropdownMenu className="dropdown-menu">
                  {item.subItems.map((subItem) => (
                    subItem.path.startsWith('http') ? (
                      <ExternalDropdownLink
                        key={subItem.path}
                        href={subItem.path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {subItem.label}
                      </ExternalDropdownLink>
                    ) : (
                      <DropdownLink
                        key={subItem.path}
                        to={subItem.path}
                      >
                        {subItem.label}
                      </DropdownLink>
                    )
                  ))}
                </DropdownMenu>
              </DropdownContainer>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                $isActive={location.pathname === item.path}
                $scrolled={scrolled}
                $isHome={isHome}
              >
                {item.label}
              </NavLink>
            )
          ))}
        </NavMenu>

        <MobileMenuButton onClick={toggleMenu} $scrolled={scrolled} $isHome={isHome} $isOpen={isMenuOpen}>
          {isMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;